/**
 * Purchase Management Types (Level 3.1)
 *
 * Tracks purchases from suppliers (inventory we buy)
 */
// === HELPER LABELS ===
export const PURCHASE_TYPE_LABELS = {
    outright: 'Outright Purchase',
    memo: 'On Memo',
    consignment: 'Consignment'
};
export const PAYMENT_STATUS_LABELS = {
    unpaid: 'Unpaid',
    partial: 'Partially Paid',
    paid: 'Paid'
};
// === COLOR CODING ===
export const PURCHASE_TYPE_COLORS = {
    outright: '#3b82f6', // Blue
    memo: '#f59e0b', // Amber
    consignment: '#8b5cf6' // Purple
};
export const PAYMENT_STATUS_COLORS = {
    unpaid: '#ef4444', // Red
    partial: '#f59e0b', // Amber
    paid: '#10b981' // Green
};
/**
 * Validates purchase data
 */
export function validatePurchase(purchase) {
    const errors = [];
    // Required fields
    if (!purchase.supplierId?.trim()) {
        errors.push({ field: 'supplierId', message: 'Supplier is required' });
    }
    if (!purchase.purchaseDate) {
        errors.push({ field: 'purchaseDate', message: 'Purchase date is required' });
    }
    if (!purchase.purchaseType) {
        errors.push({ field: 'purchaseType', message: 'Purchase type is required' });
    }
    if (!purchase.items || purchase.items.length === 0) {
        errors.push({ field: 'items', message: 'At least one line item is required' });
    }
    // Validate totals
    if (purchase.subtotal !== undefined && purchase.subtotal < 0) {
        errors.push({ field: 'subtotal', message: 'Subtotal cannot be negative' });
    }
    if (purchase.total !== undefined && purchase.total < 0) {
        errors.push({ field: 'total', message: 'Total cannot be negative' });
    }
    return {
        isValid: errors.length === 0,
        errors
    };
}
/**
 * Generates a unique purchase number
 * Format: PUR-YYYY-NNN
 * Example: PUR-2025-001
 */
export function generatePurchaseNumber(year, sequence) {
    return `PUR-${year}-${sequence.toString().padStart(3, '0')}`;
}
/**
 * Calculate subtotal from line items
 */
export function calculateSubtotal(items) {
    return items.reduce((sum, item) => sum + item.totalCost, 0);
}
/**
 * Calculate total with tax, shipping, discount
 */
export function calculateTotal(subtotal, taxAmount = 0, shippingCost = 0, discountAmount = 0) {
    return subtotal + taxAmount + shippingCost - discountAmount;
}
/**
 * Calculate amount owed
 */
export function calculateAmountOwed(total, amountPaid) {
    return Math.max(0, total - amountPaid);
}
/**
 * Determine payment status based on amounts
 */
export function determinePaymentStatus(total, amountPaid) {
    if (amountPaid === 0)
        return 'unpaid';
    if (amountPaid >= total)
        return 'paid';
    return 'partial';
}
/**
 * Check if purchase is overdue
 */
export function isPurchaseOverdue(purchase) {
    if (purchase.paymentStatus === 'paid' || !purchase.dueDate) {
        return false;
    }
    const dueDate = new Date(purchase.dueDate);
    const now = new Date();
    return now > dueDate;
}
/**
 * Format purchase for display
 */
export function formatPurchaseDisplay(purchase) {
    return `${purchase.purchaseNumber} - ${purchase.supplierName} (${purchase.items.length} items)`;
}
/**
 * Get days until due (negative if overdue)
 */
export function getDaysUntilDue(purchase) {
    if (!purchase.dueDate)
        return null;
    const dueDate = new Date(purchase.dueDate);
    const now = new Date();
    const diffTime = dueDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
}
/**
 * Create line item from diamond
 */
export function createLineItemFromDiamond(diamond, // Diamond type
quantity = 1) {
    const totalCost = (diamond.costPerCarat || 0) * diamond.caratWeight * quantity;
    return {
        diamondId: diamond.id,
        stockNumber: diamond.stockNumber,
        description: `${diamond.caratWeight}ct ${diamond.shape} ${diamond.color} ${diamond.clarity}`,
        quantity,
        costPerCarat: diamond.costPerCarat || 0,
        caratWeight: diamond.caratWeight,
        totalCost,
        diamondSnapshot: {
            shape: diamond.shape,
            caratWeight: diamond.caratWeight,
            color: diamond.color,
            clarity: diamond.clarity,
            cut: diamond.cut,
            lab: diamond.lab,
            certificateNumber: diamond.certificateNumber
        }
    };
}
